import { MusicTransport } from './music-transport.js';
import { selectedMusic, itemInstrument, videoSource, itemToVideoTime, videoToItemTime, musicToItemTime, isSynchronized } from '../core/project.js';
import { DEFAULT_INSTRUMENT, normalizeInstrument } from '../core/instruments.js';
import { clamp } from '../core/utils.js';
import { BeatAudio } from './beat-audio.js';
import { PracticeMetronome } from './metronome.js';

export class PracticeTransport {
  constructor(video, { onState, onFrame, onError }) {
    Object.assign(this, { mediaPlayer: video, onState, onFrame, onError });
    this.mode = 'video';
    this.defaultInstrument = DEFAULT_INSTRUMENT;
    this.audition = new MusicTransport({ onError });
    this.music = new MusicTransport({
      onState: state => {
        if (this.mode !== 'piano') return;
        if (state.event === 'pause' || state.event === 'waiting') this.mediaPlayer.pause();
        this.onState(state);
        this.metronome?.sync(state.event);
      },
      onFrame: state => { if (this.mode === 'piano') { this.followVideo(state); this.onFrame(state); } }, onError,
    });
    this.beats = new BeatAudio(this.music);
    this.metronome = new PracticeMetronome(this.beats, () => this.state, () => this.item);
    video.addEventListener('player-state', event => { event.stopPropagation(); if (this.mode === 'video') { this.onState({ event: event.detail.event, ...this.state }); this.metronome.sync(event.detail.event); } });
    video.addEventListener('player-frame', event => { event.stopPropagation(); if (this.mode === 'video') this.onFrame(this.state); });
  }
  get video() { return this.mediaPlayer.video; }
  get sourceKind() { return this.mode === 'piano' ? 'music' : this.mediaPlayer.sourceKind; }
  get mediaInfo() { return this.mediaPlayer.mediaInfo; }
  get loaded() { return this.mode === 'piano' ? !!selectedMusic(this.item) : this.mediaPlayer.loaded; }
  get state() {
    if (this.mode === 'piano') return this.music.state;
    const state = this.mediaPlayer.state;
    if (!this.item) return state;
    if (!this.mediaPlayer.loaded) return { ...state, time: this.item.position, duration: this.item.media.duration, rate: this.item.rate, muted: this.item.muted };
    return { ...state, time: clamp(videoToItemTime(this.item, state.time), 0, this.item.media.duration), duration: this.item.media.duration };
  }
  async load(file) { this.pause(); this.item = null; this.mode = 'video'; return this.mediaPlayer.load(file); }
  async loadYouTube(url) { this.pause(); this.item = null; this.mode = 'video'; return this.mediaPlayer.loadYouTube(url); }
  configure(item) {
    this.pause(); this.item = item;
    this.setInstrument(itemInstrument(item, this.defaultInstrument)).catch(this.onError);
    this.refreshMusic();
    this.mode = item.audioSource === 'piano' || !this.mediaPlayer.loaded ? 'piano' : 'video';
    if (videoSource(item) && !isSynchronized(item)) this.mode = 'video';
    if (!selectedMusic(item)) this.mode = 'video';
    this.setRate(item.rate); this.setMuted(item.muted); this.seek(item.position);
  }
  refreshMusic() {
    const score = selectedMusic(this.item);
    if (!this.item) return;
    const anchors = JSON.stringify([score?.anchors,this.item.musicSources.find(source => source.id === this.item.timelineMusicId)?.anchors,this.item.timeline]);
    if (score?.notes === this.lastNotes && anchors === this.lastAnchors && this.lastDuration === this.item.media.duration) return;
    this.lastNotes = score?.notes; this.lastAnchors = anchors; this.lastDuration = this.item.media.duration;
    const notes = (score?.notes || []).map(note => {
      const start = musicToItemTime(this.item, note.start, score), end = musicToItemTime(this.item, note.start + note.duration, score);
      return { ...note, start, duration: end - start };
    }).filter(note => note.start + note.duration > 0 && note.start < this.item.media.duration);
    this.music.setNotes(notes, this.item.media.duration);
  }
  async setAudio(source) {
    if (!['video','piano'].includes(source)) throw new Error('Choose video audio or music.');
    const time = this.state.time, rate = this.state.rate, muted = this.state.muted, playing = !this.state.paused;
    if (source === 'video' && !this.mediaPlayer.loaded) throw new Error('Reconnect the video to use its audio.');
    if (source === 'piano' && !selectedMusic(this.item)) throw new Error('Link music first.');
    if (source === 'piano' && videoSource(this.item) && !isSynchronized(this.item)) throw new Error('Confirm or align the music first.');
    if (source === this.mode) return;
    this.pause();
    this.mode = source; this.item.audioSource = source;
    if (videoSource(this.item)?.kind === 'youtube' && source === 'piano') this.item.display = 'music';
    this.music.seek(time); this.setRate(rate); this.setMuted(muted); this.seek(time);
    if (playing) await this.play();
  }
  play() {
    if (!this.loaded) return Promise.reject(new Error('Reconnect this video, or choose linked music to practice.'));
    const token = this.playToken = (this.playToken || 0) + 1;
    const ready = this.metronome.prepare().catch(this.onError);
    let playing;
    if (this.mode === 'piano') {
      const start = () => { if (token !== this.playToken) return; this.mediaPlayer.setMuted(true); return this.music.play(); };
      playing = this.metronome.enabled ? ready.then(start) : start();
    } else { this.music.pause(); playing = this.mediaPlayer.play(); }
    return Promise.all([ready, playing]).then(() => { if (token === this.playToken) this.metronome.sync(); });
  }
  pause() { this.playToken = (this.playToken || 0) + 1; this.metronome.stop(); this.beats.stop(); this.music.pause(); this.mediaPlayer.pause(); this.audition.pause(); }
  setMetronome(settings) { return this.metronome.configure(settings); }
  setInstrument(id) { return Promise.all([this.music.setInstrument(id), this.audition.setInstrument(id)]); }
  setDefaultInstrument(id) {
    this.defaultInstrument = normalizeInstrument(id);
    return this.setInstrument(itemInstrument(this.item, this.defaultInstrument));
  }
  previewInstrument(id = this.music.instrument) {
    this.pause();
    this.audition.setInstrument(id);
    return this.audition.previewNotes([60, 64, 67]);
  }
  previewNote(pitch) {
    // A YouTube player must not continue under replacement instrument audio.
    if (videoSource(this.item)?.kind === 'youtube' && (!this.state.paused || !this.mediaPlayer.state.paused)) this.pause();
    return this.audition.preview(pitch, this.state.muted);
  }
  seek(time) { if (this.mode === 'piano') { this.music.seek(time); this.followVideo(this.music.state, true); } else this.mediaPlayer.seek(this.item ? itemToVideoTime(this.item, time) : time); }
  seekForPlayback(time, { signal } = {}) {
    if (signal?.aborted) throw new DOMException('Playback was cancelled.', 'AbortError');
    if (this.mode === 'piano') { this.seek(time); return; }
    return new Promise((resolve, reject) => {
      const finish = error => {
        clearTimeout(timer); this.mediaPlayer.removeEventListener('player-state', check); signal?.removeEventListener('abort', abort);
        if (error) reject(error); else resolve();
      };
      const tolerance = this.mediaPlayer.sourceKind === 'youtube' ? .5 : .1;
      const check = () => { const state = this.state; if (!state.seeking && Math.abs(state.time - time) < tolerance) finish(); };
      const abort = () => finish(new DOMException('Playback was cancelled.', 'AbortError'));
      const timer = setTimeout(() => finish(new Error('Could not reach the playback position. Try Play again.')), 10000);
      this.mediaPlayer.addEventListener('player-state', check); signal?.addEventListener('abort', abort, { once: true });
      try { this.seek(time); check(); } catch (error) { finish(error); }
    });
  }
  setRate(rate) { return this.mode === 'piano' ? this.music.setRate(rate) : this.mediaPlayer.setRate(rate); }
  setMuted(muted) { if (muted) this.audition.pause(); if (this.mode === 'piano') { this.mediaPlayer.setMuted(true); this.music.setMuted(muted); } else this.mediaPlayer.setMuted(muted); }
  followVideo(state, force = false) {
    if (this.mode !== 'piano') return;
    const source = videoSource(this.item);
    if (!source || source.kind === 'youtube' || !this.mediaPlayer.loaded) return;
    if (this.item.display === 'music' || !isSynchronized(this.item)) { this.mediaPlayer.pause(); return; }
    const time = itemToVideoTime(this.item, state.time), after = itemToVideoTime(this.item, state.time + .1);
    if (time < 0 || time > source.duration || state.paused) { this.mediaPlayer.pause(); if (force) this.mediaPlayer.seek(clamp(time,0,source.duration)); return; }
    this.mediaPlayer.setMuted(true);
    this.mediaPlayer.setRate(clamp(state.rate * (after - time) / .1, .25, 2));
    if (force || Math.abs(this.mediaPlayer.state.time - time) > .18) this.mediaPlayer.seek(time);
    if (this.mediaPlayer.state.paused) this.mediaPlayer.play().catch(() => {});
  }
  showCountdown(value) { this.mediaPlayer.showCountdown(value); }
  clear() { this.pause(); this.item = null; this.mode = 'video'; this.mediaPlayer.clear(); }
  destroy() { this.clear(); this.music.destroy(); this.audition.destroy(); }
}
